import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { t } from 'i18next';
import { RoundBadge } from '@peacecover/component';
import { CartFill, PeopleFill } from '@peacecover/icon';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { useGetLatestPostsCount } from '../../../domains/lounge/ui/hooks/useGetLatestPostsCount';
import { formatDateTime } from '../../../utils/DateUtil';
import { getUnSeenCounts } from '../../utils/getUnSeenCounts';
import type { ChatRoomType } from '../types/ChatMessageType';

interface ChatVariantProps {
  variant: 'chat';
  chatRoom: ChatRoomType;
}

interface LoungeVariantProps {
  variant: 'lounge';
  categoryGroup: string;
}

type ChatItemProps = ChatVariantProps | LoungeVariantProps;

const CustomLoungeChatItem = ({ categoryGroup }: { categoryGroup: string }) => {
  const navigate = useNavigate();

  const { data: lounge } = useGetLatestPostsCount(
    localStorage.getItem(`latestPostId-${categoryGroup}`) ?? '',
    categoryGroup
  );

  const showMessage = () => {
    if (!lounge || !lounge.latestPost) return t(`ChatPage.description.${categoryGroup}`);
    const content = lounge.latestPost?.content || '';
    if (lounge.latestPost?.postType === 'Megaphone') {
      return t('ChatPage.sendMegaphone');
    }
    if ((!content || content.trim() === '') && lounge.latestPost?.hasImages) {
      return t('ChatPage.sendImage');
    }
    return content;
  };

  const handleNavigate = () => {
    navigate(`/lounge-chat?categoryGroup=${categoryGroup}`);
  };

  return (
    <button
      type="button"
      onClick={handleNavigate}
      className="flex items-center gap-[12px] px-[20px] py-[12px] w-full"
    >
      <div className="flex-none">
        <div className="relative flex items-center justify-center">
          <ProfileAvatar
            thumbnailUrl={`/assets/icons/thumbnail_lounge_${categoryGroup}.png`}
            widthAndHeight="46px"
            svgWidthAndHeight="32px"
          />
          <div className="z-10 absolute bottom-0 left-[35px] bg-[linear-gradient(90deg,#FF45A9_0%,#FF6A6A_100%)] rounded-full p-[2px] border border-pure-white dark:border-pure-black">
            <PeopleFill size={10} className="text-pure-white" />
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-[4px] items-start w-full">
        <div className="flex flex-row justify-between w-full">
          <div className="flex flex-col gap-[2px]">
            <div className="flex flex-row gap-[5px] items-center">
              <p className="h-[18px] text-[15px] font-semibold text-text-main-lv2 leading-[18px] max-w-[100px] tracking-[-0.5px] truncate">
                {t(`LoungeChatPage.${categoryGroup}`)}
              </p>
            </div>
          </div>
          <p className="h-[13px] text-[11px] text-text-sub-lv1">
            {lounge?.latestPost
              ? lounge?.latestPost.authoredAt && formatDateTime(lounge.latestPost.authoredAt * 1000)
              : null}
          </p>
        </div>
        <div className="flex flex-row items-center justify-between w-full gap-[4px]">
          <p className="text-[13px] text-text-main-lv3 leading-[16px] max-w-[240px] truncate">
            {showMessage()}
          </p>
          {typeof lounge?.latestPostsCount === 'number' &&
          lounge.latestPostsCount > 0 &&
          lounge.latestPostsCount <= 99 ? (
            <div className="h-[16px] min-w-[16px] text-on-primary bg-red-400 rounded-[42px] text-[11px] font-semibold flex items-center justify-center leading-[13px] px-[4px] py-[1.5px]">
              {lounge.latestPostsCount}
            </div>
          ) : typeof lounge?.latestPostsCount === 'number' && lounge.latestPostsCount > 99 ? (
            <div className="h-[16px] text-on-primary bg-red-400 rounded-[42px] text-[11px] font-semibold flex items-center justify-center leading-[13px] pl-[4px] pr-[5px] py-[1.5px]">
              +99
            </div>
          ) : null}
        </div>
      </div>
    </button>
  );
};

const ChatItemChat = ({ chatRoom }: { chatRoom: ChatRoomType }) => {
  const unSeenCount = getUnSeenCounts({
    unSeenCounts: chatRoom.lastMessage?.unseenCounts || [],
    userProfileId: chatRoom.myProfile.userProfileId
  });

  const category = useMemo(() => {
    if (chatRoom.isDirectMessage) {
      return t('ChatPage.category.DM');
    }
    if (chatRoom.subjectType === 'MatchedTradingPhotoCard') {
      return t('ChatPage.category.exchange');
    }
    if (chatRoom.subjectType === 'TradingPhotoCard') {
      if (chatRoom.tradingOwnerProfileId === chatRoom.myProfile.userProfileId) {
        return t('ChatPage.category.selling');
      }
      return t('ChatPage.category.purchasing');
    }
    if (chatRoom.bettieProfile) {
      return t('ChatPage.category.bettie');
    }
  }, [chatRoom]);

  const hasArtistId = useMemo(() => {
    return chatRoom.photoCard?.artistId;
  }, [chatRoom]);

  const navigateToChatRoom = () => {
    if (chatRoom.bettieProfile) {
      return `/bettie-chats/${chatRoom.chatRoomId}`;
    }
    if (chatRoom.isDirectMessage) {
      return `/chat_rooms/${chatRoom.chatRoomId}/v2`;
    }
    if (hasArtistId) {
      return `/chats/${chatRoom.chatRoomId}?artistId=${chatRoom.photoCard?.artistId}`;
    }
    return `/chats/${chatRoom.chatRoomId}`;
  };

  return (
    <Link
      key={chatRoom.chatRoomId}
      to={navigateToChatRoom()}
      state={chatRoom.bettieProfile ? { from: 'home' } : undefined}
      className="flex items-center gap-[12px] px-[20px] py-[12px]"
    >
      <div className="flex-none">
        <div className="relative flex items-center justify-center">
          <ProfileAvatar
            thumbnailUrl={
              chatRoom.traderProfile?.thumbnailUrl || chatRoom.bettieProfile?.pictureUrl
            }
            widthAndHeight="46px"
            svgWidthAndHeight="32px"
          />
          {/* DM이거나 베티인 경우 소셜 프로필 / 이외에는 양도 프로필 아이콘 */}
          {chatRoom.isDirectMessage || chatRoom.bettieProfile ? (
            <div className="z-10 absolute bottom-0 left-[35px] bg-[linear-gradient(90deg,#FF45A9_0%,#FF6A6A_100%)] rounded-full p-[2px] border border-pure-white dark:border-pure-black">
              <PeopleFill size={10} className="text-pure-white" />
            </div>
          ) : (
            <div className="z-10 absolute bottom-0 left-[35px] bg-[linear-gradient(136deg,#15CCCB_1.21%,#4987FF_50.69%,#8B1CFA_101.7%)] rounded-full p-[2px] border border-pure-white dark:border-pure-black">
              <CartFill size={10} className="text-pure-white" />
            </div>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-[4px] items-start w-full">
        <div className="flex flex-row justify-between w-full">
          <div className="flex flex-row gap-[5px] items-center">
            <p className="h-[18px] text-[15px] font-semibold text-text-main-lv2 leading-[18px] max-w-[100px] tracking-[-0.5px] truncate">
              {chatRoom.traderProfile?.nickname || chatRoom.bettieProfile?.nickname}
            </p>
            <RoundBadge className="h-[17px] text-[10px] font-semibold px-[6px] py-[2.5px]">
              {category}
            </RoundBadge>
            {/* TODO: 개별 알림 끄기 옵션 기능이 추가되면 UI 분기 처리 필요 */}
            {/* <Bell size={14} className="text-text-sub-lv2 dark:text-[#4D4D50]" /> */}
          </div>
          <p className="h-[13px] text-[11px] text-text-sub-lv1">
            {chatRoom.lastMessage && formatDateTime(chatRoom.lastMessage?.sentAt)}
          </p>
        </div>
        <div className="flex flex-row items-center justify-between w-full gap-[4px]">
          <p className="text-[13px] text-text-main-lv3 leading-[16px] max-w-[200px] truncate">
            {chatRoom.lastMessage?.content}
          </p>

          {typeof unSeenCount === 'number' && unSeenCount > 0 && unSeenCount <= 99 ? (
            <div className="h-[16px] min-w-[16px] text-on-primary bg-red-400 rounded-[42px] text-[11px] font-semibold flex items-center justify-center leading-[13px] px-[4px] py-[1.5px]">
              {unSeenCount}
            </div>
          ) : typeof unSeenCount === 'number' && unSeenCount > 99 ? (
            <div className="h-[16px] text-on-primary bg-red-400 rounded-[42px] text-[11px] font-semibold flex items-center justify-center leading-[13px] pl-[4px] pr-[5px] py-[1.5px]">
              +99
            </div>
          ) : null}
        </div>
      </div>
    </Link>
  );
};

const ChatItem = (props: ChatItemProps) => {
  if (props.variant === 'lounge') return <CustomLoungeChatItem categoryGroup={props.categoryGroup} />;
  return <ChatItemChat chatRoom={props.chatRoom} />;
};

export default ChatItem;
